import * as React from 'react';

import Button from '@/components/elements/Button/BaseButton';
import TextButton from '@/components/elements/Button/TextButton';
import { TOKUSHOU_TERMS_URL } from '@/config/constants/terms';
import { useUserContext } from '@/contexts/user';
import { CreditCardMemberStatus } from '@/lib/apollo/types/gen/graphql';
import {
  getSkyflagLink,
  getToretaChargeUrl,
  getToretaPinChargeUrl,
  PayType,
} from '@/lib/dmm/point';
import { OrderStep } from '@/modelTypes';
import { creditCardSelector } from '@/selectors/creditCardBrand';

import { MerpayIcon, PaypayIcon } from '../../Order/QuickCharge/Top/style';
import {
  BannerArea,
  ChargeMethodWrapper,
  LinkButtonArea,
  NoteArea,
  TitleWrapper,
  Wrapper,
} from './style';

interface SelectChargeProps {
  setOrderStep: React.Dispatch<React.SetStateAction<OrderStep>>;
  creditCardStatus: CreditCardMemberStatus;
}

const SelectCharge: React.FC<SelectChargeProps> = ({
  setOrderStep,
  creditCardStatus,
}) => {
  const { user, isMember } = useUserContext();
  const { isPccDmm } = creditCardSelector(user.creditCard);
  const getHrefPath = (type: PayType | 'toretaPin') => {
    if (type === 'toretaPin') {
      return getToretaPinChargeUrl();
    } else if (type === PayType.Skyflag) {
      return getSkyflagLink(user.userId);
    }
    return getToretaChargeUrl({ payType: type as PayType });
  };

  const externalChargeMethods: {
    type: PayType | 'toretaPin';
    name: JSX.Element | string;
    disabled: boolean;
  }[] = [
    {
      type: PayType.Credit,
      name: 'クレジットカード',
      disabled:
        !isPccDmm || creditCardStatus === CreditCardMemberStatus.Dormant,
    },
    {
      type: PayType.Bank,
      name: '銀行振込',
      disabled: false,
    },
    {
      type: PayType.Paypay,
      name: (
        <>
          <PaypayIcon />
          PayPay
        </>
      ),
      disabled: false,
    },
    {
      type: PayType.Merpay,
      name: (
        <>
          <MerpayIcon />
          メルペイ
        </>
      ),
      disabled: false,
    },
    {
      type: 'toretaPin',
      name: 'toreta+ 番号',
      disabled: !isMember,
    },
  ];
  return (
    <Wrapper>
      <TitleWrapper>
        <p>toreta+ チャージ方法を選択してください</p>
      </TitleWrapper>
      <ChargeMethodWrapper>
        {externalChargeMethods.map(ec => (
          <a
            key={ec.type}
            href={getHrefPath(ec.type)}
            target={ec.type === PayType.Skyflag ? '_blank' : ''}
            aria-disabled={ec.disabled}
          >
            <Button
              bgColor="secondary"
              size="s"
              height="48px"
              radius="m"
              fontWeight="bold"
              disabled={ec.disabled}
              outline
            >
              {ec.name}
            </Button>
          </a>
        ))}
      </ChargeMethodWrapper>
      <NoteArea>
        <p>
          toreta+ は決済完了後即時に利用できます。
          <br />
          toreta+
          はいかなる場合も返金または換金することはできません。予めご了承ください。
          <br />
          toreta+チャージは専用のチャージ画面で行います。
          <br />
          ※画面遷移しますが、投票パネル内の情報は維持されます。
        </p>
      </NoteArea>
      <LinkButtonArea>
        <a href={`${TOKUSHOU_TERMS_URL}#payment_method`} target="_blank">
          toreta+ の支払い方法について
        </a>
      </LinkButtonArea>
      <LinkButtonArea>
        <TextButton size="s" onClick={() => setOrderStep(OrderStep.Ready)}>
          前の画面へ戻る
        </TextButton>
      </LinkButtonArea>
      <BannerArea></BannerArea>
    </Wrapper>
  );
};

export default SelectCharge;
